import {
    type ComponentAfterAddDataEvent,
    type ComponentBeforeAddDataEvent,
    COMPONENTS_HOOKS,
    type RefComponentAfterAddDataEvent,
    SerializeDataAfterEvent
} from '../hooks'
import {pointerUtils, type CreateExtArgs} from '@wix/document-manager-core'
import _ from 'lodash'
import type {
    CompRef,
    CompStructure,
    DeserializationMappers,
    Pointer,
    SerializedCompStructure
} from '@wix/document-services-types'
import type {RefOverridesExtensionAPI} from '../../refOverrides'
import type {HooksExtensionApi} from '../../hooks/hooks'
import type {DataModelExtensionAPI} from '../../dataModel/dataModel'
import type {NicknamesExtensionAPI} from '../../nicknames'
import {getComponentType} from '../../../utils/dalUtils'
import {VIEW_MODES, DATA_TYPES, MASTER_PAGE_ID} from '../../../constants/constants'
import {getUpdatedSerializedConnections} from './connectionsHooks'
import {ReportableError} from '@wix/document-manager-utils'

const INTERNAL_REF_TYPE = 'InternalRef'

const copyOverriddenData = (
    createExtArgs: CreateExtArgs,
    compToAddPointer: Pointer,
    serializedOverriddenData: any,
    originalNicknameContext: any,
    mappers: DeserializationMappers
) => {
    const {pointers, extensionAPI} = createExtArgs
    const {refOverrides} = extensionAPI as RefOverridesExtensionAPI
    const pageId = pointers.structure.getPageOfComponent(compToAddPointer).id
    _.forEach(serializedOverriddenData, ({compId, itemType, dataItem, isMobile}) => {
        refOverrides.createOverrideDataItem(
            compToAddPointer,
            compId,
            itemType,
            pageId,
            dataItem,
            isMobile,
            originalNicknameContext,
            mappers
        )
    })
}

const generateConnectionOverrideForRefComponent = (
    createExtArgs: CreateExtArgs,
    data: ComponentAfterAddDataEvent,
    pagePointer: Pointer
) => {
    const {compToAddPointer} = data
    const {extensionAPI, dal, pointers} = createExtArgs
    const {dataModel} = extensionAPI as DataModelExtensionAPI
    const {nicknames} = extensionAPI as NicknamesExtensionAPI
    const {refOverrides} = extensionAPI as RefOverridesExtensionAPI
    const componentData = dataModel.components.getItem(compToAddPointer, 'data')
    let rootId
    let rootComponentType
    const nicknameGeneratorProvider = refOverrides.nickname.getNicknameContextGenerator()
    if (nicknameGeneratorProvider) {
        return nicknameGeneratorProvider(data)
    } else if (componentData.type === 'InternalRef') {
        rootId = componentData.rootCompId
        rootComponentType = getComponentType(dal, {id: rootId, type: VIEW_MODES.DESKTOP})
    } else {
        const {appDefinitionId, widgetId} = componentData
        const remoteWidgetMetaData = dal.get(
            pointers.remoteStructureMetaData.getRemoteStructureWidgetMetaData(appDefinitionId, widgetId)
        )
        if (!remoteWidgetMetaData) {
            throw new Error('Need to load remote widget meta data for nickname creation')
        }
        rootId = remoteWidgetMetaData.rootId
        rootComponentType = remoteWidgetMetaData.rootType
    }
    nicknames.generateConnectionOverrideForRefComponentRoot(compToAddPointer, pagePointer, rootId, rootComponentType)
}

export const handleDataOverridesAfterAdd = (createExtArgs: CreateExtArgs, data: ComponentAfterAddDataEvent) => {
    const {extensionAPI, pointers} = createExtArgs
    const {compToAddPointer, componentDefinition, mappers} = data
    // @ts-ignore
    const {overriddenData, originalNicknameContext} = componentDefinition.custom || {}
    if (overriddenData) {
        copyOverriddenData(createExtArgs, compToAddPointer, overriddenData, originalNicknameContext, mappers)
    }
    const pagePointer = pointers.structure.getPageOfComponent(compToAddPointer)
    generateConnectionOverrideForRefComponent(createExtArgs, data, pagePointer)
    const {hooks} = extensionAPI as HooksExtensionApi
    hooks.executeHook(
        COMPONENTS_HOOKS.ADD_COMPONENT.REF_COMPONENT_AFTER.createEvent(data as unknown as RefComponentAfterAddDataEvent)
    )
}

const filterConnectionOverrides = (overriddenData: any) => _.filter(overriddenData, {itemType: DATA_TYPES.connections})

const updateConnectionOverridesControllerIds = (
    createExtArgs: CreateExtArgs,
    overriddenData: any,
    containerPointer: Pointer,
    componentDefinition: CompStructure,
    oldToNewIdMap: Record<string, string>
) => {
    const connectionOverrideItems = filterConnectionOverrides(overriddenData)
    _.forEach(connectionOverrideItems, item => {
        const updatedConnectionItems = getUpdatedSerializedConnections(
            createExtArgs,
            containerPointer as CompRef,
            componentDefinition as SerializedCompStructure,
            item.dataItem.items,
            false,
            oldToNewIdMap
        )
        if (updatedConnectionItems) {
            item.dataItem.items = updatedConnectionItems
        }
    })
}

const getSlottedCompIdsFromOverridenData = (overriddenData: any[]): string[] => {
    const compIds: string[] = []
    for (const override of overriddenData ?? []) {
        if (override?.itemType === DATA_TYPES.slots) {
            compIds.push(...(Object.values(override?.dataItem?.slots ?? {}) as string[]))
        }
    }

    return compIds
}

export const referredComponentBeforeAdd = (createExtArgs: CreateExtArgs, data: ComponentBeforeAddDataEvent) => {
    const {componentDefinition, containerPointer, mappers} = data

    const {overriddenData} = componentDefinition.custom || {}

    if (overriddenData) {
        // filter out children that are not slotted components, to prevent display only components from being deserialized
        const slottedCompIds = new Set(getSlottedCompIdsFromOverridenData(overriddenData))
        componentDefinition.components = componentDefinition.components
            ? componentDefinition.components.filter(comp => slottedCompIds.has((comp as CompStructure).id!))
            : []
    } else {
        componentDefinition.components = []
    }

    if (overriddenData) {
        updateConnectionOverridesControllerIds(
            createExtArgs,
            overriddenData,
            containerPointer,
            componentDefinition,
            mappers?.oldToNewIdMap
        )
    }
}

const internalRefCompTypeWhiteList = new Set([
    'wysiwyg.viewer.components.MenuContainer',
    'responsive.components.HeaderSection',
    'responsive.components.FooterSection'
])

export const findMatchingLocalRootCompIdForInternalRef = (
    createExtArgs: CreateExtArgs,
    data: ComponentBeforeAddDataEvent
) => {
    if (data?.componentDefinition?.data?.type === INTERNAL_REF_TYPE) {
        const {rootCompType, rootCompId} = data.componentDefinition.data
        delete data.componentDefinition.data.rootCompType

        const originalRootCompIdExists = createExtArgs.dal.has(pointerUtils.getPointer(rootCompId, VIEW_MODES.DESKTOP))
        if (!originalRootCompIdExists && rootCompType) {
            if (internalRefCompTypeWhiteList.has(rootCompType)) {
                const matchingComponentsByType = createExtArgs.pointers.structure.getComponentsByTypeForPage(
                    rootCompType,
                    MASTER_PAGE_ID
                )
                if (matchingComponentsByType.length > 0) {
                    const matchingComponent = matchingComponentsByType[0]
                    data.componentDefinition.data.rootCompId = matchingComponent.id
                } else {
                    throw new ReportableError({
                        errorType: 'InternalRefRootCompNotFound',
                        message: `Attempted to add InternalRef referencing component of type ${rootCompType}, but no matching component was found`,
                        extras: {rootCompType, componentDefinition: data.componentDefinition}
                    })
                }
            } else {
                throw new ReportableError({
                    errorType: 'InternalRefRootCompTypeNotWhitelisted',
                    message: `Attempted to add InternalRef referencing component of type ${rootCompType}, but this type is not whitelisted for the rootCompType field`,
                    extras: {rootCompType, componentDefinition: data.componentDefinition}
                })
            }
        }
    }
}

export const addRootCompTypeToInternalRef = (createExtArgs: CreateExtArgs, data: SerializeDataAfterEvent) => {
    if (data?.componentDefinition?.data?.type === INTERNAL_REF_TYPE) {
        const {rootCompId} = data.componentDefinition.data
        const targetCompPointer = pointerUtils.getPointer(rootCompId, VIEW_MODES.DESKTOP)

        const rootCompType = getComponentType(createExtArgs.dal, targetCompPointer)
        if (internalRefCompTypeWhiteList.has(rootCompType)) {
            data.componentDefinition.data.rootCompType = rootCompType
            delete data.componentDefinition.data.rootCompId
        }
    }
}
